import { useMemo } from 'react';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import flatMap from 'lodash/flatMap';

import { SECONDS_IN_DAY } from '@cast/constants';
import { SortDirection } from '@cast/design-system';
import {
  NoDataReason,
  WorkloadEfficiencyTopReportItem,
  WorkloadsEfficiencyReportItem,
} from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOADS_COST_EFFICIENCY_QUERY_KEY = 'workloads-cost-efficiency';

const getPaginatedWorkloadsEfficiencyQueryKey = ({
  clusterId,
  startTime,
  endTime,
  useListingPrices,
  namespaces,
  workloadNames,
  workloadTypes,
  labels,
  pageLimit,
  sortBy,
  sortDirection,
}: Partial<PaginatedWorkloadsEfficiencyQueryArgs> = {}) =>
  [
    WORKLOADS_COST_EFFICIENCY_QUERY_KEY,
    ...(clusterId !== undefined
      ? [
          clusterId,
          startTime,
          endTime,
          useListingPrices,
          namespaces,
          workloadNames,
          workloadTypes,
          JSON.stringify(labels),
          pageLimit,
          sortBy,
          sortDirection,
        ]
      : []),
  ] as const;

export type PaginatedWorkloadsEfficiencyQueryArgs = {
  clusterId: string;
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  stepSeconds?: number;
  enabled?: boolean;
  pageLimit?: number;
  labels?: Array<{ label?: string; value?: string }>;
  namespaces?: string[];
  workloadNames?: string[];
  workloadTypes?: string[];
  sortBy?: string;
  sortDirection?: SortDirection;
};

type PaginatedWorkloadsEfficiencyQuery = {
  workloads?: WorkloadsEfficiencyReportItem[];
  topWorkloads?: WorkloadEfficiencyTopReportItem[];
  metricsServerAvailable?: boolean;
  count?: number;
  noDataReason?: NoDataReason;
  isLoading: boolean;
  error?: AxiosError | null;
  refetch: () => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isFetching: boolean;
};

const getPaginatedWorkloadsEfficiencyQueryOptions = ({
  clusterId,
  startTime,
  endTime,
  useListingPrices,
  stepSeconds = SECONDS_IN_DAY,
  enabled = true,
  pageLimit = 50,
  namespaces,
  workloadNames,
  workloadTypes,
  sortBy,
  sortDirection,
  labels,
}: PaginatedWorkloadsEfficiencyQueryArgs) =>
  infiniteQueryOptions({
    queryKey: getPaginatedWorkloadsEfficiencyQueryKey({
      clusterId,
      startTime,
      endTime,
      useListingPrices,
      namespaces,
      workloadNames,
      workloadTypes,
      labels,
      pageLimit,
      sortBy,
      sortDirection,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.costReport.getWorkloadEfficiency({
        clusterId,
        startTime,
        endTime,
        filter: {
          workloadNames,
          workloadTypes,
          namespaces,
          labels,
        },
        stepSeconds,
        pageLimit: String(pageLimit),
        pageCursor: pageParam,
        sortField: sortBy,
        sortOrder: sortDirection,
        useListingPrices,
      });

      return data;
    },
    initialPageParam: '',
    getNextPageParam: ({ nextCursor }) =>
      nextCursor?.length ? nextCursor : undefined,
    enabled: !!clusterId && enabled,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
  });

export const usePaginatedWorkloadsEfficiencyQuery = (
  props: PaginatedWorkloadsEfficiencyQueryArgs
): PaginatedWorkloadsEfficiencyQuery => {
  const {
    isLoading,
    data,
    error,
    refetch,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery(getPaginatedWorkloadsEfficiencyQueryOptions(props));

  const workloads = useMemo(
    () => flatMap(data?.pages, (page) => page.items || []),
    [data]
  );

  const topWorkloads = useMemo(
    () =>
      flatMap(
        data?.pages,
        (page) =>
          page.topItems?.map((item) => ({
            ...item,
            costImpactHistory: item.costImpactHistory?.map((item) => ({
              ...item,
              timestamp: dayjs
                .utc(item.timestamp)
                .subtract(1, 'day')
                .toISOString(),
            })),
          })) || []
      ),
    [data]
  );

  const lastPage = data?.pages.at(-1);

  return {
    workloads,
    topWorkloads,
    count: parseInt(lastPage?.count || '0', 10),
    noDataReason: lastPage?.noDataReason,
    metricsServerAvailable: lastPage?.metricsServerAvailable,
    isLoading,
    error,
    refetch,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
  };
};
